import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  clearCreateNewReport,
  clearReplaceReport,
  INextStepType,
  setNextStep,
} from '../../../redux/slices/cachedEntities';
import { useAppDispatch } from '../../../redux/hooks';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { getQueryTypePagePath } from '../../../utils';
import SectionHeading from '../../SectionHeading';
import NextStepItem from './NextStepItem';
import { nextStepsInfo } from '../../KubitTooltip/globalTooltips';
import { assertCondition } from '../../../utils/types';
import useCachedEntitiesSelector from '../../../redux/selectorHooks/useCachedEntitiesSelector';
import { batch } from 'react-redux';
import ThinDivider from '../../ThinDivider';
import { useChartContext } from '../ChartContext';

const ListWrapper = styled.div`
  display: flex;
  column-gap: 16px;
`;

const QUERY_TYPES_WITH_NEXT_STEPS: INextStepType[] = [
  QueryType.Expression,
  QueryType.Funnel,
  QueryType.Path,
  QueryType.Retention,
  QueryType.DataTable,
];

const NextSteps = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const createNewReport = useCachedEntitiesSelector('createNewReport');
  const replaceReport = useCachedEntitiesSelector('replaceReport');

  const {
    metadata: { query, timeUnit, startDate, endDate, datePicker, queryType },
  } = useChartContext();

  const availableNextSteps = useMemo(() => {
    return QUERY_TYPES_WITH_NEXT_STEPS.filter((type) => type !== queryType);
  }, [queryType]);

  const handleClick = (type: INextStepType) => {
    batch(() => {
      assertCondition(
        query.__typename !== 'CohortSampleQuery' &&
          query.__typename !== 'SignificanceQuery' &&
          query.__typename !== 'PredictionQuery',
      );
      dispatch(
        setNextStep({
          builderInfo: query,
          targetType: type,
          dateInput: {
            startDate,
            endDate,
            datePicker,
          },
          timeUnit,
        }),
      );

      if (replaceReport) {
        dispatch(clearReplaceReport());
      }

      if (createNewReport) {
        dispatch(clearCreateNewReport());
      }
    });

    navigate(getQueryTypePagePath(type));
  };

  return (
    <>
      <ThinDivider layout="horizontal" size="full" />
      <SectionHeading heading="Next Steps" tooltipOption={nextStepsInfo} />
      <ListWrapper>
        {availableNextSteps.map((type) => (
          <NextStepItem
            key={`next-step-${type}`}
            queryType={type}
            onClick={() => handleClick(type)}
          />
        ))}
      </ListWrapper>
    </>
  );
};

export default NextSteps;
